import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'utils/types';
// styles
import styled, { css } from 'styled-components';
import { media } from 'utils/style';
// components
import { Spacer } from '@ontruck/orc';
import QuoteMessageError from 'components/QuoteMessageError';
import PriceButton from 'components/Price/Button';
import TotalPrice from 'components/Price/TotalPrice';
import LoadSpinner from 'components/LoadSpinner';
import LiteralCurrency from 'components/LiteralCurrency';
import DiffPriceWhenEdit from 'components/Price/DiffPriceWhenEdit';
import PriceConceptsBreakdown from 'components/PriceConceptsBreakdown';
import PricingDisclaimer from 'components/Price/PricingDisclaimer';
import { PriceSuggestStyled } from './styled';
import { useTranslation } from 'react-i18next';
import useIsHandlingAllowed from 'hooks/useIsHandlingAllowed';
import QuoteMessageNonFeasible from 'components/Price/QuoteMessageNonFeasible';
import { useFeasibility } from 'hooks/useFeasibility';
import NonFeasibleEstimationMessage from 'components/NonFeasibleEstimationMessage';
import useFormState from 'hooks/useFormState';
import { CloseIcon, GirdedInfoIcon } from 'refactored/ui/icons';

const ResponsiveToggleStyled = styled.div`
  background-color: white;
  width: 100%;
  & > svg {
    display: none;
  }
  ${media.lessThan('large')`
    border-top: 1px solid #E2E8EE;
    padding: 16px;
    padding-right: 64px;
    background-color: ${(props) => (!props.background ? '#E0EAFF' : 'white')};
    & > svg {
      display: block;
      position: absolute;
      right: 24px;
      top: 24px;
      cursor: pointer;
      & path {
        fill:  #94A6B8;
      }
    }
    ${({ background }) =>
      background &&
      css`
      .background {
        position absolute;
        bottom:0;
        left: 0;
        z-index: -1;
        height: 100vh;
        width: 100%;
        background: rgba(0,0,0,0.6);
      }
    `}
    ${({ isHidden }) =>
      isHidden &&
      css`
        display: none;
      `}
    ${({ onlyMessage }) =>
      onlyMessage &&
      css`
        & > svg {
          display: none;
        }
      `}
  `};
`;

const ResponsiveToggle = ({
  className,
  children,
  isHidden,
  toggleDropdown,
  onlyMessage,
}) => {
  let background;
  children.forEach((child) => {
    if (child?.props?.className === 'breakdown-not-collapsed ') {
      background = true;
    } else {
      background = false;
    }
  });

  return (
    <ResponsiveToggleStyled
      background={background}
      isHidden={isHidden}
      onlyMessage={onlyMessage}
      className={className}
    >
      <div className="background" onClick={toggleDropdown}></div>
      <CloseIcon onClick={toggleDropdown} />
      {children}
    </ResponsiveToggleStyled>
  );
};
ResponsiveToggle.propTypes = {
  className: PropTypes.string,
  isHidden: PropTypes.bool,
  toggleDropdown: PropTypes.func,
  onlyMessage: PropTypes.bool,
};

const QuotePrice = ({
  className,
  title,
  isFetching,
  isEditing,
  isSubmitting,
  isDataReady,
  hasError,
  errors,
  totalPrice,
  fullPrice,
  collapsable,
  withMargin,
  showPrice,
  showTotal,
  withSubmitButton,
  submitButtonLabel,
  onSubmit,
  canShowError,
  priceConcepts,
  moreThanOneDayQuote,
}) => {
  const { t } = useTranslation('Quote');
  const { showFeasibilityMessage } = useFeasibility();
  const { enabledContinueBtn, validateForm, isFormInvalid } = useFormState();
  const showPriceInfo = useMemo(
    () => isDataReady && !isFetching && !showFeasibilityMessage,
    [isDataReady, isFetching, showFeasibilityMessage]
  );
  const showNonFeasibleMessage = useMemo(
    () => isDataReady && !isFetching && showFeasibilityMessage,
    [isDataReady, isFetching, showFeasibilityMessage]
  );
  const showNonFeasibleEstimationMessage = useMemo(() => {
    return showNonFeasibleMessage && !!totalPrice;
  }, [showNonFeasibleMessage, totalPrice]);
  const showQuoteMessageError = useMemo(
    () => hasError && canShowError,
    [hasError, canShowError]
  );
  const isHandlingAllowed = useIsHandlingAllowed();
  const validateAndSubmit = () => {
    validateForm();
    if (!isFormInvalid) onSubmit();
  };
  const disableButton =
    !enabledContinueBtn || isSubmitting || !isEmpty(errors) || isFetching;

  const buttonTextTranslated = showFeasibilityMessage
    ? t('continue_non_feasible_order')
    : t('continue');

  const [isHidden, setIsHidden] = useState(true);
  const toggleDropdown = useCallback(() => {
    setIsHidden(!isHidden);
  }, [setIsHidden, isHidden]);
  const onlyMessage =
    !showNonFeasibleEstimationMessage && showNonFeasibleMessage;
  useEffect(() => {
    if (onlyMessage) {
      setIsHidden(false);
    }
  }, [
    setIsHidden,
    showNonFeasibleEstimationMessage,
    showNonFeasibleMessage,
    onlyMessage,
  ]);

  const showSuggestions = !isFormInvalid && !!showPrice && !isFetching;

  return (
    <div className={className} data-testid="quote-price">
      <h1>{title}</h1>
      {/* NOT POSSIBLE */}
      {showQuoteMessageError && <QuoteMessageError />}

      <ResponsiveToggle
        isHidden={isHidden}
        toggleDropdown={toggleDropdown}
        onlyMessage={onlyMessage}
      >
        {/* ESTIMACION */}
        <div className="non-feasible-estimation-message-container">
          {showNonFeasibleMessage && <QuoteMessageNonFeasible />}
        </div>
        {/* PRICE BREAKDOWN */}
        {!isFormInvalid && showPriceInfo && isEmpty(errors) && (
          <PriceConceptsBreakdown
            className={`${collapsable ? 'breakdown-not-collapsed' : ''} ${
              withMargin ? 'withMargin' : ''
            }`}
            concepts={priceConcepts}
            collapsable={collapsable}
            t={t}
            showTooltips
          />
        )}
      </ResponsiveToggle>

      <div className="fixed-heigth-component">
        {/* LOADING */}
        {isFetching && (
          <Spacer mBottom="M">
            <LoadSpinner
              loadText={t('load_text')}
              loadTitle={t('load_title')}
              skin="standar"
            />
          </Spacer>
        )}
        {/* ESTIMATION PRICE */}
        {showNonFeasibleEstimationMessage && (
          <NonFeasibleEstimationMessage
            toggleDropdown={toggleDropdown}
            price={totalPrice}
          />
        )}

        {/* PRECIO SIN IVA */}
        {showPriceInfo && isEmpty(errors) && (
          <div className="vat-container">
            {!!showPrice && !isFormInvalid && (
              <TotalPrice
                t={t}
                toggleDropdown={toggleDropdown}
                priceTitle={t('no_vat_included')}
                priceConcepts={priceConcepts}
                totalPrice={totalPrice}
                collapsable={collapsable}
                moreThanOneDayQuote={moreThanOneDayQuote}
              />
            )}
            {!!showTotal && !!fullPrice && (
              <div className="vat-value">
                <LiteralCurrency value={fullPrice} /> {t('including_vat')}
              </div>
            )}
            {!!showPrice && <DiffPriceWhenEdit />}
          </div>
        )}

        {/* ACTIVE BUTTON */}
        {!hasError && withSubmitButton && (
          <PriceButton
            onClick={validateAndSubmit}
            disabled={disableButton}
            text={submitButtonLabel || buttonTextTranslated}
          />
        )}
      </div>
      {!hasError && withSubmitButton && (isHidden || onlyMessage) && (
        <>
          {/* ERROR */}
          {!enabledContinueBtn && (
            <div className="button-msg-error">
              <GirdedInfoIcon />
              <p>{t('incomplete_form_error')}</p>
            </div>
          )}
          {/* TRANSPORT LAW */}
          {!isHandlingAllowed && (
            <div className="button-msg-warning">
              <div className="icon-container">
                <GirdedInfoIcon />
              </div>
              <p>{t('HandlingExtra:transport_law_warning')}</p>
            </div>
          )}
        </>
      )}

      <PricingDisclaimer />

      {showSuggestions && (
        <PriceSuggestStyled
          showNonFeasibleEstimationMessage={showNonFeasibleEstimationMessage}
        />
      )}
    </div>
  );
};

QuotePrice.propTypes = {
  isDataReady: PropTypes.bool.isRequired,
  hasError: PropTypes.bool,
  isFetching: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  className: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  fullPrice: PropTypes.number,
  totalPrice: PropTypes.number,
  isHidden: PropTypes.bool.isRequired,
  priceConcepts: PropTypes.arrayOf(
    PropTypes.shape({
      concept: PropTypes.string.isRequired,
      tooltipInnerComponent: PropTypes.func,
      conceptQuantity: PropTypes.number.isRequired,
    })
  ),
  onSubmit: PropTypes.func,
  title: PropTypes.string,
  showPrice: PropTypes.bool,
  showTotal: PropTypes.bool,
  withSubmitButton: PropTypes.bool,
  submitButtonLabel: PropTypes.string,
  collapsable: PropTypes.bool,
  withMargin: PropTypes.bool,
  errors: PropTypes.shape({
    error: PropTypes.string,
  }),
  canShowError: PropTypes.bool,
  moreThanOneDayQuote: PropTypes.bool,
};

QuotePrice.defaultProps = {
  hasError: false,
  isFetching: false,
  isSubmitting: false,
  totalPrice: 0,
  fullPrice: 0,
  onSubmit: () => ({}),
  priceConcepts: [],
  title: '',
  showTotal: false,
  showPrice: true,
  withSubmitButton: true,
  collapsable: true,
  withMargin: false,
  errors: {},
  submitButtonLabel: '',
  canShowError: true,
  moreThanOneDayQuote: false,
};

export default QuotePrice;
